import { BlockStack, Button, InlineStack, Text } from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import { getRequest, putRequest } from "../../../shared/components/functions";

export const PartsScan = ({ id }) => {
  const [loaded, setLoaded] = useState(false);
  const [scans, setScans] = useState([]);
  const [serial, setSerial] = useState(null);
  const [highlighted, setHighlighted] = useState(new Set());
  const [removing, setRemoving] = useState(new Set());

  const requiredParts = [{ name: "Loadcell" }, { name: "RFID reader" }, { name: "Valve block" }, { name: "Boiler" }];

  const fetchSerial = useCallback(async () => {
    const data = await getRequest(`/api/serials/${id}`);
    if (data) {
      setSerial(data);
      // Prefer ScannedParts (array stored as JSON string). Fallback to OperatorInteractions.scannedParts
      let scanned = [];
      try {
        scanned = data?.ScannedParts ? JSON.parse(data.ScannedParts || "[]") : [];
      } catch (e) {
        scanned = [];
      }
      if ((!scanned || scanned.length === 0) && data?.OperatorInteractions) {
        try {
          const operatorInteractions = JSON.parse(data.OperatorInteractions || "{}");
          scanned = operatorInteractions?.scannedParts || [];
        } catch (e) {
          scanned = [];
        }
      }

      const incoming = Array.isArray(scanned) ? scanned : [];
      setScans((prev) => {
        const prevSet = new Set(prev);
        const newItems = incoming.filter((p) => !prevSet.has(p));
        if (newItems.length > 0) {
          setHighlighted((old) => {
            const copy = new Set(old);
            newItems.forEach((p) => copy.add(p));
            return copy;
          });
          setTimeout(() => {
            setHighlighted((prevHL) => {
              const copy = new Set(prevHL);
              newItems.forEach((p) => copy.delete(p));
              return copy;
            });
          }, 1500);
        }
        return incoming;
      });
    }
    setLoaded(true);
  }, [id]);

  useEffect(() => {
    fetchSerial();
    const interval = setInterval(fetchSerial, 1000);
    return () => clearInterval(interval);
  }, [id, fetchSerial]);

  const handleRemovePart = useCallback(
    async (part) => {
      if (!serial) return;

      // mark as removing for animation + button loading
      setRemoving((prev) => {
        const copy = new Set(prev);
        copy.add(part);
        return copy;
      });

      const updated = scans.filter((p, idx) => p !== part || scans.indexOf(p) !== scans.indexOf(part));
      const payload = {
        serialnumber: serial.serialnumber,
        qualityCheckDate: serial.qualityCheckDate || "",
        qualityCheckByUserID: serial.qualityCheckByUserID || null,
        remarks: serial.remarks || "",
        softwareTypeID: serial.softwareTypeID || null,
        hardwareTypeID: serial.hardwareTypeID || null,
        recipeTypeID: serial.recipeTypeID || null,
        productTypeID: serial.productTypeID || null,
        hardwareBoilerTypeID: serial.hardwareBoilerTypeID || null,
        Status: serial.Status || "",
        OperatorInteractions: serial.OperatorInteractions || "",
        S3ObjectKeys: serial.S3ObjectKeys || "",
        ScannedParts: JSON.stringify(updated),
        Checks: serial.Checks || "",
        RfidLocation: serial.RfidLocation || "",
      };

      try {
        await putRequest(`/api/serials/${id}`, payload);
        // delay removal slightly to let fade/scale animation play
        setTimeout(() => {
          setScans(updated);
          setRemoving((prev) => {
            const copy = new Set(prev);
            copy.delete(part);
            return copy;
          });
        }, 250);
      } catch (e) {
        // revert removing state on error
        setRemoving((prev) => {
          const copy = new Set(prev);
          copy.delete(part);
          return copy;
        });
      }
    },
    [id, scans, serial]
  );

  return loaded ? (
    <BlockStack>
      <InlineStack align="center">
        <Text variant="heading2xl" as="h3">
          Scanned parts
        </Text>
      </InlineStack>

      <BlockStack gap="10">
        {Array.isArray(scans) && scans.length > 0
          ? scans.map((part, idx) => {
              const isHighlighted = highlighted.has(part);
              const isRemoving = removing.has(part);
              return (
                <div
                  key={`${part}-${idx}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 10px",
                    borderRadius: "12px",
                    background: isRemoving ? "#fff3f3" : isHighlighted ? "#bbf7d0" : "transparent",
                    transition: "background 900ms ease, opacity 220ms ease, transform 180ms ease",
                    opacity: isRemoving ? 0.6 : 1,
                    transform: isRemoving ? "scale(0.98)" : "scale(1)",
                  }}
                >
                  <Text as="h4" variant="headingLg">{part}</Text>
                  <Button size="medium" loading={isRemoving} onClick={() => handleRemovePart(part)}>Remove</Button>
                </div>
              );
            })
          : (
              <Text>Waiting for scans...</Text>
            )}
      </BlockStack>
    </BlockStack>
  ) : null;
};
